import api from './api';
import type { AnalysisData, DebtEvent } from '../pages/RestrictiveAnalysis/types';

// Flag de modo debug
const IS_DEBUG = false;

interface HistoryEntry {
  documento: string;
  timestamp: number;
  data: AnalysisData;
}


function transformAssertivaResponse(payload: any): AnalysisData {
  if (!payload) {
    throw new Error('Resposta vazia da Assertiva.');
  }

 
  const responseBody = payload.response_body || {};
  const presentation = payload.presentation || {};
  const supplemental = payload.supplemental_bodies || {};
  const localizeData = supplemental.pf_localize?.resposta || supplemental.pj_localize?.resposta || {};
  const dadosPessoais = presentation.dados_pessoais || localizeData.dadosCadastrais || {};
  const localizacaoContatos = presentation.localizacao_e_contatos || {};
  const score = responseBody.resposta?.score || {};
  const debitos = responseBody.resposta?.registrosDebitos || { list: [], valorTotal: 0 };
  const ultimasConsultas = responseBody.resposta?.ultimasConsultas || { list: [], qtdUltConsultas: 0 };
  const protestos = responseBody.resposta?.protestosPublicos || { list: [], qtdProtestos: 0 };
  const acoes = supplemental.pf_acoes?.resposta?.acoes || supplemental.pj_acoes?.resposta?.acoes || [];
  
  const nomeCompleto =
    payload.nome ||
    presentation.nome ||
    dadosPessoais.nome ||
    localizeData.dadosCadastrais?.nome ||
    'Documento sem nome registrado';

  const situacaoReceitaRaw =
    dadosPessoais.situacao_receita_federal ||
    dadosPessoais.situacaoCadastral ||
    '';

  const situacaoReceita: 'Regular' | 'Irregular' =
    (String(situacaoReceitaRaw).toUpperCase().includes('REGULAR') ||
      String(situacaoReceitaRaw).toUpperCase().includes('ATIVA'))
      ? 'Regular'
      : 'Irregular';

  const enderecoFromPresentation = Array.isArray(localizacaoContatos.endereco)
    ? localizacaoContatos.endereco[0]
    : localizacaoContatos.endereco;
  const enderecoFromLocalize = Array.isArray(localizeData.enderecos)
    ? localizeData.enderecos[0]
    : undefined;
  const enderecoFromResposta = Array.isArray(responseBody.resposta?.enderecos)
    ? responseBody.resposta.enderecos[0]
    : undefined;
  const endereco = enderecoFromPresentation || enderecoFromLocalize || enderecoFromResposta || {};

  const emailsPresentation = Array.isArray(localizacaoContatos.emails)
    ? localizacaoContatos.emails
    : [];
  const emailsLocalize = Array.isArray(localizeData.emails)
    ? localizeData.emails
    : [];
  const emails = emailsPresentation.length > 0
    ? emailsPresentation.map((item: any) => (typeof item === 'string' ? item : item?.email)).filter(Boolean)
    : emailsLocalize.map((item: any) => item?.email).filter(Boolean);

  const telefonesPresentation = Array.isArray(localizacaoContatos.telefones)
    ? localizacaoContatos.telefones
    : [];
  const telefonesLocalizeFixos = Array.isArray(localizeData.telefones?.fixos)
    ? localizeData.telefones.fixos.map((item: any) => item?.numero).filter(Boolean)
    : [];
  const telefonesLocalizeMoveis = Array.isArray(localizeData.telefones?.moveis)
    ? localizeData.telefones.moveis.map((item: any) => item?.numero).filter(Boolean)
    : [];
  const telefonesRespostaFixos = Array.isArray(responseBody.resposta?.telefones?.fixos)
    ? responseBody.resposta.telefones.fixos.map((item: any) => item?.numero).filter(Boolean)
    : [];
  const telefonesRespostaMoveis = Array.isArray(responseBody.resposta?.telefones?.moveis)
    ? responseBody.resposta.telefones.moveis.map((item: any) => item?.numero).filter(Boolean)
    : [];
  const telefones = telefonesPresentation.length > 0
    ? telefonesPresentation
    : [...telefonesLocalizeFixos, ...telefonesLocalizeMoveis, ...telefonesRespostaFixos, ...telefonesRespostaMoveis];

  const participacoesRaw =
    (Array.isArray(presentation.participacoes_societarias) && presentation.participacoes_societarias.length > 0
      ? presentation.participacoes_societarias
      : localizeData.participacoesEmpresas) || [];

  const rendaPresumida =
    Number(presentation.renda_presumida?.[0]?.valor) ||
    Number(presentation.faturamento_estimado?.valor) ||
    Number(responseBody.resposta?.faturamentoEstimado?.valor) ||
    0;

  const consultasUltimos90Dias =
    Number(ultimasConsultas.qtdUltConsultas) ||
    (Array.isArray(ultimasConsultas.list) ? ultimasConsultas.list.length : 0) ||
    (Array.isArray(presentation.ultimas_consultas) ? presentation.ultimas_consultas.length : 0);
  
  const eventosDebitos: DebtEvent[] = [];
  
  (acoes || []).forEach((acao: any, idx: number) => {
    if (acao.valor) {
      eventosDebitos.push({
        id: `acao-${idx}`,
        tipo: 'acoesJudiciais',
        dataEvento: acao.dataAcao || new Date().toLocaleDateString('pt-BR'),
        valor: parseFloat(acao.valor) || 0,
        descricaoTitulo: acao.descricao || 'Ação Judicial',
        origem: acao.vara || acao.foro || 'Sem informação',
        natureza: acao.natureza || 'Judicial',
      });
    }
  });

  (protestos.list || []).forEach((protesto: any, idx: number) => {
    if (protesto.valor) {
      eventosDebitos.push({
        id: `protesto-${idx}`,
        tipo: 'protestos',
        dataEvento: protesto.dataProtesto || new Date().toLocaleDateString('pt-BR'),
        valor: parseFloat(protesto.valor) || 0,
        descricaoTitulo: protesto.descricao || 'Protesto',
        origem: protesto.tabeliao || '1º Tabelião',
        natureza: 'Protesto',
      });
    }
  });

  (debitos.list || []).forEach((debito: any, idx: number) => {
    if (debito.valor) {
      eventosDebitos.push({
        id: `debito-${idx}`,
        tipo: 'registroDebitos',
        dataEvento: debito.dataInclusao || new Date().toLocaleDateString('pt-BR'),
        valor: parseFloat(debito.valor) || 0,
        descricaoTitulo: debito.credor || 'Registro de Débito',
        origem: debito.credor || 'Credor desconhecido',
        natureza: debito.tipoDebito || 'Débito',
      });
    }
  });

  const analisisData: AnalysisData = {
    nomeCompleto,
    idade: Number(dadosPessoais.idade) || 0,
    dataNascimento: dadosPessoais.data_nascimento || dadosPessoais.dataNascimento || 'Não disponível',
    documento: presentation.cpf || presentation.cnpj || dadosPessoais.cpf || dadosPessoais.cnpj || 'N/A',
    situacaoReceita,
    dataConsulta: responseBody.cabecalho?.dataHora 
      ? responseBody.cabecalho.dataHora.split(' ')[0] 
      : new Date().toLocaleDateString('pt-BR'),
    endereco: {
      logradouro: endereco.logradouro || '',
      numero: String(endereco.numero || ''),
      bairro: endereco.bairro || '',
      cidade: endereco.cidade || '',
      uf: endereco.uf || '',
      cep: endereco.cep || '',
    },
    emails,
    telefones,
    score: score.pontuacao || score.pontos || 0,
    classeRisco: score.classificacao || score.classe || 'F',
    descricaoRisco: score.faixa?.descricao || 'Classificação não disponível',
    participacoesEmpresas: (participacoesRaw || []).map((emp: any, idx: number) => ({
      id: String(idx),
      nomeFantasia: emp.nomeFantasia || emp.razao_social || emp.razaoSocial || emp.nome || 'Empresa sem nome',
      status: emp.status === 'ativa' || emp.status === 'Ativa' ? 'Ativa' : 'Ativa',
      cnpj: emp.cnpj || emp.cnpj_normalizado || '',
      razaoSocial: emp.razaoSocial || emp.razao_social || emp.nome || '',
      dataEntrada: emp.dataEntrada || emp.data_entrada || '',
      cnae: emp.cnae || '',
      email: emp.email || '',
    })),
    rendaPresumida,
    consultasUltimos90Dias,
    dividas: {
      acoesJudiciaisTotal: acoes.reduce((sum: number, a: any) => sum + (parseFloat(a.valor) || 0), 0),
      protestosTotal: protestos.list?.reduce((sum: number, p: any) => sum + (parseFloat(p.valor) || 0), 0) || 0,
      chequesQuantidade: responseBody.resposta?.cheques?.length || 0,
      chequesTotal: responseBody.resposta?.cheques?.reduce((sum: number, c: any) => sum + (parseFloat(c.valor) || 0), 0) || 0,
      registroDebitosTotal: debitos.valorTotal || 0,
      registroDebitosDetalhes: (debitos.list || []).map((debito: any) => ({
        dataInclusao: debito.dataInclusao || '',
        credor: debito.credor || '',
        descricao: `${debito.tipoDebito || 'Débito'} - Contrato: ${debito.contrato || 'N/A'}`,
      })),
    },
    eventosDividas: eventosDebitos,
  };

  return analisisData;
}

function normalizeAndValidateAnalysisPayload(payload: unknown): AnalysisData {
  if (typeof payload === 'string' && payload.trim().startsWith('<!DOCTYPE html')) {
    throw new Error('A API retornou HTML em vez de JSON. Verifique REACT_APP_API_URL e o proxy para o backend Laravel.');
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Resposta inválida da API de análise restritiva.');
  }

  const raw = payload as Record<string, unknown>;

  
  if (typeof raw.nomeCompleto === 'string' && typeof raw.documento === 'string') {
    return payload as AnalysisData;
  }

  
  if (raw.response_body || raw.presentation) {
    return transformAssertivaResponse(payload);
  }

  throw new Error('Formato de resposta não mapeado para a tela de análise restritiva.');
}


const requestHistory: HistoryEntry[] = [];


function getFromHistory(document: string): AnalysisData | null {
  const now = Date.now();
  const fiveMinutesMs = 5 * 60 * 1000;

  const entry = requestHistory.find(
    (item) => item.documento === document && (now - item.timestamp) < fiveMinutesMs
  );

  return entry?.data || null;
}


function addToHistory(document: string, data: AnalysisData): void {
  const index = requestHistory.findIndex((item) => item.documento === document);
  if (index > -1) {
    requestHistory.splice(index, 1);
  }

  requestHistory.push({
    documento: document,
    timestamp: Date.now(),
    data,
  });
}


function getDebugData(document: string): AnalysisData {
  return {
    nomeCompleto: 'JOÃO MÁRIO DE ANDRADAS',
    idade: 45,
    dataNascimento: '15/03/1979',
    documento: document,
    situacaoReceita: 'Regular',
    dataConsulta: new Date().toLocaleDateString('pt-BR'),
    endereco: {
      logradouro: 'Rua das Flores',
      numero: '123',
      bairro: 'Centro',
      cidade: 'São Paulo',
      uf: 'SP',
      cep: '01310-100',
    },
    emails: ['joao.mario@email.com'],
    telefones: ['(11) 98765-4321'],
    score: 181,
    classeRisco: 'E',
    descricaoRisco: 'Alto risco',
    participacoesEmpresas: [
      {
        id: '1',
        nomeFantasia: 'ABC SERVIÇOS DE COBRANÇA LTDA',
        status: 'Ativa',
        cnpj: '12.345.678/0001-90',
        razaoSocial: 'ABC SERVIÇOS DE COBRANÇA LTDA',
        dataEntrada: '2020-01-15',
        cnae: '6910-4/00',
        email: 'contato@abccobranca.com.br',
      },
      {
        id: '2',
        nomeFantasia: 'ABC SERVIÇOS DE COBRANÇA & CIA',
        status: 'Inativa',
        cnpj: '12.345.678/0002-71',
        razaoSocial: 'ABC SERVIÇOS DE COBRANÇA & CIA',
        dataEntrada: '2019-05-20',
        cnae: '6910-4/00',
        email: 'info@abccia.com.br',
      },
    ],
    rendaPresumida: 5000,
    consultasUltimos90Dias: 12,
    dividas: {
      acoesJudiciaisTotal: 7910.0,
      protestosTotal: 303.63,
      chequesQuantidade: 0,
      chequesTotal: 0,
      registroDebitosTotal: 2500.0,
      registroDebitosDetalhes: [
        {
          dataInclusao: '2024-01-10',
          credor: 'Banco XYZ',
          descricao: 'Débito de cartão de crédito',
        },
      ],
    },
    eventosDividas: [
      {
        id: '1',
        tipo: 'acoesJudiciais',
        dataEvento: '2024-03-01',
        valor: 7910.0,
        descricaoTitulo: 'Ação Judicial de Cobrança',
        origem: '7ª Vara Cível',
        natureza: 'Cobrança',
      },
      {
        id: '2',
        tipo: 'protestos',
        dataEvento: '2024-02-15',
        valor: 303.63,
        descricaoTitulo: 'Protesto de Título',
        origem: '1º Tabelião de Notas e Protestos',
        natureza: 'Protesto',
      },
    ],
  };
}


function detectDocumentType(digits: string): 'CPF' | 'CNPJ' {
  return digits.length === 11 ? 'CPF' : 'CNPJ';
}


async function consultarAnalise(document: string): Promise<AnalysisData> {
  const digits = document.replace(/\D/g, '');
  const docType = detectDocumentType(digits);


  if (IS_DEBUG) {
    const url = docType === 'CPF'
      ? '/api/adm/assertiva/restrictive-pf'
      : '/api/adm/assertiva/restrictive-pj';
    console.log(`[MONEY-SAVED] Debug mode active. Request to ${url} blocked.`);
  }


  if (IS_DEBUG) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const data = getDebugData(digits);
        addToHistory(digits, data);
        resolve(data);
      }, 1500);
    });
  }

 
  const historyData = getFromHistory(digits);
  if (historyData) {
    console.log(`[ASSERTIVA] Consulta recuperada do histórico (sem custo adicional). Documento: ${digits}`);
    return historyData;
  }

 
  try {
    if (docType === 'CPF') {
      const response = await api.post<AnalysisData>(
        '/api/adm/assertiva/restrictive-pf',
        {
          cpf: digits,
          subject_id: 1,
        }
      );
      const data = normalizeAndValidateAnalysisPayload(response.data);
      addToHistory(digits, data);
      return data;
    } else {
      
      const response = await api.post<AnalysisData>(
        '/api/adm/assertiva/restrictive-pj',
        {
          cnpj: digits,
          id_finalidade: 1, 
          subject_id: 1,
          acoes: true, 
          positivo: true, 
          force_refresh: false,
        }
      );
      const data = normalizeAndValidateAnalysisPayload(response.data);
      addToHistory(digits, data);
      return data;
    }
  } catch (error) {
    throw error;
  }
}


async function obterHistorico(document: string): Promise<AnalysisData | null> {
  const digits = document.replace(/\D/g, '');
  const docType = detectDocumentType(digits);
  const paramName = docType === 'CPF' ? 'cpf' : 'cnpj';
  const endpoint = docType === 'CPF'
    ? `/api/adm/assertiva/restrictive-pf/history?${paramName}=${digits}`
    : `/api/adm/assertiva/restrictive-pj/history?${paramName}=${digits}`;

  try {
    if (IS_DEBUG) {
      console.log(`[MONEY-SAVED] Debug mode active. Request to ${endpoint} blocked.`);
      return null;
    }

    const response = await api.get<any>(endpoint);
    const data = response.data;

    if (data && Array.isArray(data.data) && data.data.length > 0) {
      const successRecord = data.data.find((item: any) => item.outcome === 'success');
      
      if (successRecord && successRecord.score_resumo) {
        console.log(`[ASSERTIVA] Consulta recuperada do histórico (sem custo adicional). Documento: ${digits}`);
        
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error(`Erro ao obter histórico (${docType}):`, error);
    return null;
  }
}


function clearHistory(): void {
  requestHistory.length = 0;
}

export const assertivaService = {
  consultarAnalise,
  obterHistorico,
  getFromHistory,
  addToHistory,
  clearHistory,
  detectDocumentType,
};
